// src/pages/MetricSettingsPage.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Button, Typography, Paper, Table, TableContainer, TableHead, TableBody,
    TableRow, TableCell, Switch, IconButton, Dialog, DialogTitle, DialogContent,
    TextField, DialogActions, FormControl, InputLabel, Select, MenuItem,
    FormHelperText, CircularProgress, Alert,
    Container,
    Stack,
    Card,
    CardContent,
    CardActions,
    Chip,
    Skeleton,
    Tab,
    Tabs
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    Business as BusinessIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon
} from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useParams } from 'react-router-dom';
import { Store } from '../../../../core/interfaces/store.interface';
import { useMetricsConfigCore } from '../../../../core/modules/metrics_config';
import { MetricConfiguration, MetricName } from '../../../../core/modules/metrics_config/interfaces/get-metrics-config';
import { MetricConfigurationCreate } from '../../../../core/modules/metrics_config/interfaces/post-metrics-config';
// Reusa el wizard completo de Google Sheets tal cual -- es contenido
// puro (sin Page/Header propio), ya lee companyId de useParams().id,
// que coincide con el :id de esta misma ruta (/dashboard/settings/:id).
// Pedido explicito de Seba: vive ACA, dentro de una pestaña
// 'Integraciones' de la Configuracion de la empresa, visible para
// company_admin Y superadmin -- no en una pantalla propia ni en el
// menu lateral (eso se saco).
import GoogleSheetsPage from '../googleSheets/GoogleSheetsPage';

const METRIC_NAMES_READABLE: Record<MetricName, string> = {
    stock_cero: "Stock Cero",
    baja_rotacion: "Baja Rotación",
    stock_critico: "Stock Crítico",
    sobre_stock: "Sobre Stock",
    recomendacion_compra: "Recomendación de Compra",
    devoluciones: "Devoluciones",
    ajuste_stock: "Ajuste de Stock",
    venta_sin_stock: "Venta Sin Stock"
};

const METRIC_PARAM_DEFINITIONS: Record<MetricName, { key: string; label: string; type: 'number' | 'text' }[]> = {
    stock_critico: [
        { key: 'days_for_avg', label: 'Días para Promedio de Venta', type: 'number' },
        { key: 'coverage_days_threshold', label: 'Días de Cobertura Crítica', type: 'number' },
        { key: 'stock_qty_threshold', label: 'Umbral Mínimo de Unidades', type: 'number' },
    ],
    sobre_stock: [
        { key: 'days_for_avg', label: 'Días para Promedio de Venta', type: 'number' },
        { key: 'coverage_days_threshold', label: 'Días de Cobertura para Sobre-Stock', type: 'number' },
    ],
    baja_rotacion: [
        { key: 'days_since_last_sale', label: 'Días sin Venta para Baja Rotación', type: 'number' },
    ],
    recomendacion_compra: [
        { key: 'sales_days_for_recommendation', label: 'Días de Venta para Recomendación', type: 'number' },
    ],
    stock_cero: [], // Sin parámetros configurables
    devoluciones: [],
    ajuste_stock: [],
    venta_sin_stock: [],
};

interface ConfigCardProps {
    config: MetricConfiguration;
    warehouseName?: string;
    onEdit: () => void;
    onDelete: () => void;
    isMutating: boolean;
}
// Componente Principal
export default function MetricSettingsPage() {
    const { id } = useParams();
    const [warehouses, setWarehouses] = useState<Store[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isMutating, setIsMutating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [tabValue, setTabValue] = useState(0);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingConfig, setEditingConfig] = useState<MetricConfigurationCreate | null>(null);
    const [currentEditingId, setCurrentEditingId] = useState<number | null>(null); // Para saber si estamos editando o creando

    const companyId = id ? parseInt(id) : 0;

    const { stores, getStores } = useCompanyCore();
    const { getMetricsConfig, postMetricsConfig, putMetricsConfig, deleteMetricsConfig, metricsConfig } = useMetricsConfigCore();

    // Función para cargar los datos desde la API
    const fetchData = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        setError(null);
        try {
            await Promise.all([getStores(companyId.toString()), getMetricsConfig(companyId.toString())]);
        } catch (err) {
            setError('Error al cargar las configuraciones.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, getStores, getMetricsConfig]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    const handleOpenForm = (config?: MetricConfiguration) => {
        if (config) {
            // Editando
            setCurrentEditingId(config.id);
            setEditingConfig({
                metric_name: config.metric_name,
                warehouse_id: config.warehouse_id,
                config_json: config.config_json,
                is_active: config.is_active,
            });
        } else {
            setCurrentEditingId(null);
            setEditingConfig({
                metric_name: 'stock_critico',
                warehouse_id: null,
                config_json: {},
                is_active: true,
            });
        }
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingConfig(null);
        setCurrentEditingId(null);
    };


    const handleSave = async (configToSave: MetricConfigurationCreate) => {
        if (!companyId) return;
        setIsMutating(true);
        setError(null);
        try {
            await postMetricsConfig(companyId.toString(), configToSave);
            handleCloseForm();
            await fetchData();
        } catch (err) {
            setError('Error al guardar la configuración.');
            console.error(err);
        } finally {
            setIsMutating(false);
        }
    };

    const handleDelete = async (configId: number) => {
        if (!companyId || !window.confirm('¿Estás seguro de que quieres eliminar esta configuración?')) return;
        setIsMutating(true);
        setError(null);
        try {
            await deleteMetricsConfig(companyId.toString(), configId.toString());
            await fetchData();
        } catch (err) {
            setError('Error al eliminar la configuración.');
            console.error(err);
        } finally {
            setIsMutating(false);
        }
    };

    const warehouseMap = useMemo(() => {
        const map = new Map<number, string>();
        warehouses.forEach(w => map.set(parseInt(w.id), w.name));
        return map;
    }, [warehouses]);


    return (
        <Container maxWidth="lg" className="py-8">
            <Typography variant="h4" component="h1" className="font-bold flex items-center gap-2 mb-4">
                <SettingsIcon fontSize="large" />
                Configuración de la empresa
            </Typography>

            <Tabs
                value={tabValue}
                onChange={(_e, v) => setTabValue(v)}
                indicatorColor="secondary"
                textColor="secondary"
                sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Métricas" />
                <Tab label="Integraciones" />
            </Tabs>

            <div hidden={tabValue !== 0}>
                <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                    <Typography variant="h6" component="h2" className="font-bold">
                        Configuración de Métricas
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()}>
                        Nueva Configuración
                    </Button>
                </Box>

                {error && <Alert severity="error" className="mb-4">{error}</Alert>}

                {isLoading ? (
                    // Loader tipo Skeleton
                    <Stack spacing={2}>
                        <Skeleton variant="rectangular" height={160} />
                        <Skeleton variant="rectangular" height={160} />
                        <Skeleton variant="rectangular" height={160} />
                    </Stack>
                ) : (
                    // Contenido real
                    <Stack spacing={3}>
                        {metricsConfig.length > 0 ? (
                            metricsConfig.map((config) => (
                                <ConfigCard
                                    key={config.id}
                                    config={config}
                                    warehouseName={config.warehouse_id ? warehouseMap.get(config.warehouse_id) : undefined}
                                    onEdit={() => handleOpenForm(config)}
                                    onDelete={() => handleDelete(config.id)}
                                    isMutating={isMutating}
                                />
                            ))
                        ) : (
                            <Paper className="p-8 text-center text-gray-500">
                                <Typography>No hay configuraciones definidas. ¡Crea la primera!</Typography>
                            </Paper>
                        )}
                    </Stack>
                )}

                {editingConfig && (
                    <ConfigurationForm
                        open={isFormOpen}
                        onClose={handleCloseForm}
                        onSave={handleSave}
                        isSaving={isMutating} // Pasar estado de guardado
                        config={editingConfig}
                        warehouses={stores}
                    />
                )}
            </div>

            <div hidden={tabValue !== 1}>
                <GoogleSheetsPage />
            </div>
        </Container>
    );
}

// --- Formulario en un Diálogo (Modal) ---
interface ConfigurationFormProps {
    open: boolean;
    onClose: () => void;
    onSave: (config: MetricConfigurationCreate) => void;
    isSaving: boolean;
    config: MetricConfigurationCreate;
    warehouses: Store[];
}

function ConfigCard({ config, warehouseName, onEdit, onDelete, isMutating }: ConfigCardProps) {
    return (
        <Card variant="outlined" className="w-full">
            <CardContent>
                <Stack spacing={2}>
                    <Box className="flex justify-between items-start">
                        <Box>
                            <Typography variant="h6" component="div" className="font-bold">
                                {METRIC_NAMES_READABLE[config.metric_name]}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" className="flex items-center gap-1">
                                <BusinessIcon fontSize="small" />
                                {config.warehouse_id ? warehouseName : 'Global (Toda la Empresa)'}
                            </Typography>
                        </Box>
                        <Chip
                            icon={config.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                            label={config.is_active ? 'Activa' : 'Inactiva'}
                            color={config.is_active ? 'success' : 'default'}
                            size="small"
                            variant="outlined"
                        />
                    </Box>
                </Stack>
            </CardContent>
            <CardActions className="justify-end">
                <IconButton onClick={onEdit} disabled={isMutating} color="primary" aria-label="editar">
                    <EditIcon />
                </IconButton>
                <IconButton onClick={onDelete} disabled={isMutating} color="error" aria-label="eliminar">
                    <DeleteIcon />
                </IconButton>
            </CardActions>
        </Card>
    );
}

function ConfigurationForm({  open, onClose, onSave, isSaving, config: initialConfig, warehouses }: ConfigurationFormProps) {
    const [config, setConfig] = useState<MetricConfigurationCreate>(initialConfig);

    useEffect(() => {
        setConfig(initialConfig);
    }, [initialConfig]);

    const handleMetricChange = (e: any) => {
        const newMetricName = e.target.value as MetricName;
        setConfig({
            ...config,
            metric_name: newMetricName,
            config_json: {}
        });
    };

    const handleJsonParamChange = (key: string, value: string) => {
        setConfig({
            ...config,
            config_json: {
                ...config.config_json,
                [key]: Number(value)
            }
        });
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(config);
    };

    const currentMetricParams = METRIC_PARAM_DEFINITIONS[config.metric_name];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" component="form" onSubmit={handleSubmit}>
            <DialogTitle className="font-bold">
                {config.metric_name ? 'Editar Configuración' : 'Nueva Configuración'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} className="pt-4">
                    <FormControl fullWidth>
                        <InputLabel id="metric-name-label">Métrica</InputLabel>
                        <Select
                            labelId="metric-name-label"
                            value={config.metric_name}
                            label="Métrica"
                            onChange={handleMetricChange}
                        >
                            {Object.entries(METRIC_NAMES_READABLE).map(([key, value]) => (
                                <MenuItem key={key} value={key as MetricName}>{value}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="warehouse-label">Sucursal</InputLabel>
                        <Select
                            labelId="warehouse-label"
                            value={config.warehouse_id === null ? 'global' : config.warehouse_id}
                            label="Sucursal"
                            onChange={(e) => setConfig({ ...config, warehouse_id: e.target.value === 'global' ? null : Number(e.target.value) })}
                        >
                            <MenuItem value="global">Global (Toda la Empresa)</MenuItem>
                            {warehouses.map(w => (
                                <MenuItem key={w.id} value={parseInt(w.id)}>{w.name}</MenuItem>
                            ))}
                        </Select>
                        <FormHelperText>Define si la configuración es para una sucursal o para toda la empresa.</FormHelperText>
                    </FormControl>

                    {currentMetricParams.length > 0 ? (
                        currentMetricParams.map(param => (
                            <TextField
                                key={param.key}
                                label={param.label}
                                type={param.type}
                                value={config.config_json[param.key] || ''}
                                onChange={(e) => handleJsonParamChange(param.key, e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                            />
                        ))
                    ) : (
                        <Typography variant="body2" color="text.secondary">Esta métrica no tiene parámetros configurables.</Typography>
                    )}

                    <FormControl component="fieldset">
                        <label className="flex items-center cursor-pointer">
                            <Switch
                                checked={config.is_active}
                                onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
                                color="primary"
                            />
                            <Typography>Activar esta configuración</Typography>
                        </label>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions className="p-4">
                <Button onClick={onClose} disabled={isSaving}>Cancelar</Button>
                <Button type="submit" variant="contained" disabled={isSaving} startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}>
                    {isSaving ? 'Guardando...' : 'Guardar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}